import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PartnershipOpportunities = () => {
  const opportunities = [
    {
      id: 1,
      title: 'Become a Verified Supplier',
      description: 'Join our network of 2,500+ verified suppliers and access a global customer base across 45+ countries.',
      icon: 'Award',
      color: 'bg-primary',
      benefits: [
        'Global market exposure',
        'Verified badge credibility',
        'Lead generation support',
        'Marketing assistance'
      ],
      cta: 'Apply as Supplier'
    },
    {
      id: 2,
      title: 'Strategic Alliance Program',
      description: 'Partner with NCHI to expand your service offerings and create mutually beneficial business relationships.',
      icon: 'Handshake',
      color: 'bg-secondary',
      benefits: [
        'Co-marketing opportunities',
        'Revenue sharing models',
        'Joint venture support',
        'Technology integration'
      ],
      cta: 'Explore Alliance'
    },
    {
      id: 3,
      title: 'Distribution Partnership',
      description: 'Become an authorized distributor for our trade facilitation services in your region.',
      icon: 'Network',
      color: 'bg-accent',
      benefits: [
        'Exclusive territories',
        'Commission structure',
        'Training & support',
        'Marketing materials'
      ],
      cta: 'Join Distribution'
    },
    {
      id: 4,
      title: 'Technology Integration',
      description: 'Integrate your platform with our trade network API for seamless data exchange and enhanced capabilities.',
      icon: 'Code',
      color: 'bg-success',
      benefits: [
        'API documentation',
        'Technical support',
        'White-label options',
        'Custom integration'
      ],
      cta: 'View API Docs'
    }
  ];

  const partnershipProcess = [
    {
      step: 1,
      title: 'Submit Application',
      description: 'Complete our partnership application form with your business details and requirements.',
      icon: 'FileText'
    },
    {
      step: 2,
      title: 'Evaluation & Verification',
      description: 'Our team reviews your application and conducts necessary verification checks.',
      icon: 'Search'
    },
    {
      step: 3,
      title: 'Agreement & Onboarding',
      description: 'Sign partnership agreement and complete onboarding process with dedicated support.',
      icon: 'FileSignature'
    },
    {
      step: 4,
      title: 'Launch & Support',
      description: 'Go live with full access to resources, training, and ongoing partnership support.',
      icon: 'Rocket'
    }
  ];

  const testimonials = [
    {
      id: 1,
      name: 'Rajesh Kumar',
      company: 'TechSource Electronics Ltd.',
      role: 'Managing Director',
      content: 'Partnering with NCHI has opened doors to markets we never thought possible. Their network and support have been instrumental in our 40% growth this year.',
      rating: 5
    },
    {
      id: 2,
      name: 'Maria Garcia',
      company: 'European Textiles GmbH',
      role: 'CEO',
      content: 'The verification process gave us credibility, and the platform connected us with quality buyers. We have seen a significant increase in international orders.',
      rating: 5
    }
  ];

  return (
    <section className="py-12 md:py-16 lg:py-20 bg-muted/30">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="text-center mb-8 md:mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full text-sm md:text-base text-primary mb-4">
            <Icon name="Handshake" size={20} />
            <span>Partnership Opportunities</span>
          </div>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-4">
            Grow Your Business With Us
          </h2>
          <p className="text-base md:text-lg text-muted-foreground max-w-3xl mx-auto">
            Explore diverse partnership models designed to create mutual value and long-term success
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 mb-12 md:mb-16">
          {opportunities?.map((opportunity) => (
            <div
              key={opportunity?.id}
              className="bg-card rounded-2xl p-6 md:p-8 shadow-lg border border-border hover:shadow-xl transition-all duration-300"
            >
              <div className={`w-14 h-14 md:w-16 md:h-16 ${opportunity?.color} rounded-xl flex items-center justify-center mb-6`}>
                <Icon name={opportunity?.icon} size={28} color="white" />
              </div>

              <h3 className="text-xl md:text-2xl font-bold text-foreground mb-3">
                {opportunity?.title}
              </h3>
              <p className="text-sm md:text-base text-muted-foreground mb-6">
                {opportunity?.description}
              </p>

              <div className="mb-6">
                <h4 className="text-sm font-semibold text-foreground mb-3">Key Benefits</h4>
                <ul className="space-y-2">
                  {opportunity?.benefits?.map((benefit, index) => (
                    <li key={index} className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Icon name="Check" size={16} color="var(--color-success)" />
                      {benefit}
                    </li>
                  ))}
                </ul>
              </div>

              <Button
                variant="default"
                fullWidth
                iconName="ArrowRight"
                iconPosition="right"
              >
                {opportunity?.cta}
              </Button>
            </div>
          ))}
        </div>

        <div className="bg-card rounded-2xl p-6 md:p-8 lg:p-12 shadow-lg border border-border mb-12 md:mb-16">
          <div className="text-center mb-8 md:mb-12">
            <h3 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
              Partnership Process
            </h3>
            <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
              Simple, transparent steps to become our valued partner
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {partnershipProcess?.map((process, index) => (
              <div key={process.step} className="relative">
                <div className="flex flex-col items-center text-center">
                  <div className="w-16 h-16 md:w-20 md:h-20 bg-primary/10 rounded-full flex items-center justify-center mb-4 relative">
                    <Icon name={process.icon} size={28} color="var(--color-primary)" />
                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center text-sm font-bold">
                      {process.step}
                    </div>
                  </div>
                  <h4 className="text-base md:text-lg font-bold text-foreground mb-2">
                    {process.title}
                  </h4>
                  <p className="text-xs md:text-sm text-muted-foreground">
                    {process.description}
                  </p>
                </div>
                {index < partnershipProcess?.length - 1 && (
                  <div className="hidden lg:block absolute top-10 left-full w-full h-0.5 bg-border -translate-x-1/2"></div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="mb-12 md:mb-16">
          <div className="text-center mb-8">
            <h3 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
              Partner Success Stories
            </h3>
            <p className="text-base md:text-lg text-muted-foreground">
              Hear from our successful partners
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {testimonials?.map((testimonial) => (
              <div
                key={testimonial?.id}
                className="bg-card rounded-2xl p-6 md:p-8 shadow-lg border border-border"
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial?.rating)]?.map((_, i) => (
                    <Icon key={i} name="Star" size={20} color="var(--color-warning)" />
                  ))}
                </div>
                <p className="text-sm md:text-base text-muted-foreground mb-6 italic">
                  "{testimonial?.content}"
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                    <Icon name="User" size={24} color="var(--color-primary)" />
                  </div>
                  <div>
                    <div className="font-semibold text-foreground">{testimonial?.name}</div>
                    <div className="text-xs md:text-sm text-muted-foreground">
                      {testimonial?.role}, {testimonial?.company}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-gradient-to-br from-primary via-secondary to-primary/90 rounded-2xl p-8 md:p-12 text-white text-center shadow-xl">
          <Icon name="Rocket" size={48} className="mx-auto mb-6" />
          <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4">
            Ready to Partner With Us?
          </h3>
          <p className="text-base md:text-lg text-white/90 max-w-2xl mx-auto mb-8">
            Join our growing network of successful partners and unlock new opportunities for your business growth
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              variant="default"
              size="lg"
              iconName="FileText"
              iconPosition="left"
              className="bg-white text-primary hover:bg-white/90"
            >
              Start Application
            </Button>
            <Button
              variant="outline"
              size="lg"
              iconName="Phone"
              iconPosition="left"
              className="border-white text-white hover:bg-white/10"
            >
              Schedule Consultation
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PartnershipOpportunities;